<template>
  <div class="stream-form-page">
    <header class="page-hero">
      <div>
        <h4 class="page-hero__title mb-1">
          {{ isEdit ? 'Настройки стрима' : 'Создать стрим' }}
        </h4>
        <p v-if="isEdit && streamData" class="page-hero__subtitle mb-0">
          {{ streamData.name || `#${streamData.id}` }}
        </p>
        <p v-else-if="!isEdit" class="page-hero__subtitle mb-0">
          Настройте новый канал публикаций
        </p>
      </div>
      <button type="button" class="btn btn-light btn-sm page-hero__back" @click="goBack">
        <i class="bi bi-arrow-left me-1"></i>Назад
      </button>
    </header>

    <!-- Загрузка данных стрима (только edit-режим) -->
    <div v-if="loadingStream" class="form-card load-state">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Загрузка...</span>
      </div>
      <span class="ms-2 text-muted">Загрузка данных стрима...</span>
    </div>

    <div v-else-if="loadError" class="form-card load-state load-state--col">
      <div class="alert alert-danger mb-3">{{ loadError }}</div>
      <router-link to="/streams" class="btn btn-secondary btn-sm">Вернуться к списку</router-link>
    </div>

    <div v-else class="form-card">
      <div class="split-body">
        <!-- Левая панель -->
        <div class="left-panel">
          <form @submit.prevent="onSave">
            <div class="generation-cloud-title generation-cloud-title--left">Основные настройки</div>

            <!-- Проект сайта (только WordPress) -->
            <div v-if="isWpPublishing" class="mb-2">
              <label class="left-headers form-label mb-0">Проект сайта</label>
              <div class="d-flex gap-2 align-items-center">
                <select class="form-select form-select-sm site-project-select"
                  v-model="selectedSiteProjectId" :disabled="siteProjectsLoading"
                  @change="onSiteProjectChange">
                  <option value="">Не привязан</option>
                  <option v-for="p in siteProjects" :key="p.id" :value="p.id">{{ p.label }}</option>
                </select>
                <router-link to="/tools/wordpress-concept" class="btn btn-outline-secondary btn-sm">
                  Проекты
                </router-link>
              </div>
              <small class="text-muted d-block">Заполняет название, описание, URL сайта и пользователя WordPress; используется для автоматической генерации.</small>
              <div v-if="siteProjectsError" class="text-danger small mt-1">{{ siteProjectsError }}</div>
            </div>

            <div class="mb-2">
              <label for="streamName" class="left-headers form-label mb-0">Название*</label>
              <input id="streamName" v-model="local.name" type="text"
                class="form-control form-control-sm" required />
            </div>

            <div class="mb-2">
              <label for="streamDescription" class="left-headers form-label mb-0">Описание</label>
              <textarea id="streamDescription" v-model="local.description"
                class="form-control form-control-sm" rows="2"></textarea>
            </div>

            <!-- Тип: редактируемый (create) или только для чтения (edit) -->
            <div class="mb-2">
              <template v-if="!isEdit">
                <label for="streamType" class="left-headers form-label mb-0">Тип стрима*</label>
                <select id="streamType" v-model="localType" class="form-select form-select-sm" required>
                  <option disabled value="">Выберите тип</option>
                  <option v-for="type in types" :key="type" :value="type">{{ type }}</option>
                </select>
              </template>
              <template v-else>
                <div class="d-flex align-items-center gap-2">
                  <label class="left-headers form-label mb-0">Тип стрима:</label>
                  <span :class="['stream-type-badge', streamTypeBadgeClass]">
                    <span class="stream-type-badge__dot"></span>
                    {{ streamType }}
                  </span>
                </div>
              </template>
            </div>

            <!-- Настройки Telegram / Яндекс.Дзен -->
            <div v-if="isTgPublishing" class="mb-2">
              <div class="mt-2 mb-1">
                <label class="left-headers form-label mb-0">Имя канала</label>
                <input type="text" class="form-control form-control-sm"
                  v-model="local.settings.chatId" placeholder="@my_channel" />
              </div>
              <div class="left-headers form-label mb-0">Телеграм бот</div>
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-outline-primary btn-sm" @click="showCredentialModal = true">
                  Выбрать / изменить
                </button>
                <button v-if="local.settings.telegramCredential" type="button"
                  class="btn btn-outline-secondary btn-sm" @click="clearCredential">
                  Очистить
                </button>
              </div>
              <div v-if="local.settings.telegramCredential" class="mt-1 text-muted small">
                выбран: {{ local.settings.telegramCredential.botName || 'без имени' }}
                ({{ local.settings.telegramCredential.label || '—' }})
              </div>
            </div>

            <!-- Настройки WordPress -->
            <div v-if="isWpPublishing" class="mb-2">
              <div class="mt-2 mb-1">
                <label class="left-headers form-label mb-0">URL сайта</label>
                <input type="url" class="form-control form-control-sm"
                  v-model="local.settings.siteUrl" placeholder="https://example.com" />
              </div>
              <div class="left-headers form-label mb-0">Пользователь WordPress</div>
              <div class="d-flex gap-2">
                <button type="button" class="btn btn-outline-primary btn-sm" @click="showCredentialModal = true">
                  Выбрать / изменить
                </button>
                <button v-if="local.settings.wordpressCredential" type="button"
                  class="btn btn-outline-secondary btn-sm" @click="clearCredential">
                  Очистить
                </button>
              </div>
              <div v-if="local.settings.wordpressCredential" class="mt-1 text-muted small">
                выбран: {{ local.settings.wordpressCredential.login || 'без логина' }}
                ({{ local.settings.wordpressCredential.label || '—' }})
              </div>
            </div>

            <CredentialModal
              v-if="showCredentialModal"
              :type="credentialType"
              :selected="isWpPublishing ? local.settings.wordpressCredential : local.settings.telegramCredential"
              @close="showCredentialModal = false"
              @select="onCredentialSelected"
            />

            <!-- AI и модель: для НЕ-WordPress остаётся в левой панели -->
            <AiModelFields
              v-if="!isWpPublishing && (streamType || isEdit)"
              v-model:ai-provider="local.aiProvider"
              v-model:ai-model="local.aiModel"
              v-model:settings="local.settings"
              :required="aiRequired"
              :ai-providers="aiProviders"
              :ai-models="aiModels"
              :reasoning-setting="reasoningSetting"
            />
          </form>
        </div>

        <!-- Правая панель -->
        <div class="right-panel">
          <!-- WordPress: вкладки авто / ручная -->
          <template v-if="isWpPublishing">
            <ul class="nav nav-tabs gen-tabs">
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: activeTab === 'auto' }"
                  @click="activeTab = 'auto'">
                  Автоматическая генерация
                </button>
              </li>
              <li class="nav-item">
                <button type="button" class="nav-link" :class="{ active: activeTab === 'manual' }"
                  @click="activeTab = 'manual'">
                  Ручная генерация
                </button>
              </li>
            </ul>

            <!-- Авто-генерация (cmaker + расписание) -->
            <div v-show="activeTab === 'auto'" class="tab-pane-body">
              <div v-if="!isEdit" class="alert alert-info py-2 px-3 small mb-3">
                Расписание и запуск станут доступны после создания стрима. Заполните проект сайта,
                источники и параметры — они сохранятся вместе со стримом.
              </div>

              <!-- Step 1 — when -->
              <section class="schedule-step">
                <div class="step-head">
                  <span class="step-num">1</span>
                  <div class="step-head__text">
                    <div class="step-title">Общие настройки</div>
                    <div class="step-sub">Активность, режим и расписание</div>
                  </div>
                </div>
                <div class="step-body">
                  <div class="schedule-grid">
                    <div class="schedule-field">
                      <label class="left-headers form-label mb-0">Активно</label>
                      <div class="form-check form-switch schedule-switch">
                        <input id="schedule-enabled" class="form-check-input" type="checkbox" v-model="form.enabled" />
                        <label class="form-check-label" for="schedule-enabled">
                          {{ form.enabled ? 'Расписание включено' : 'Расписание выключено' }}
                        </label>
                      </div>
                    </div>
                    <div class="schedule-field">
                      <label class="left-headers form-label mb-0">Режим</label>
                      <div class="mode-switch mt-1">
                        <span :class="{ 'mode-switch__label--active': form.mode === 'GENERATE_ONLY' }">
                          Только генерация
                        </span>
                        <div class="form-check form-switch mode-switch__control">
                          <input
                            id="schedule-mode"
                            class="form-check-input"
                            type="checkbox"
                            :checked="form.mode === 'GENERATE_AND_PUBLISH'"
                            @change="form.mode = $event.target.checked ? 'GENERATE_AND_PUBLISH' : 'GENERATE_ONLY'"
                          />
                          <label class="visually-hidden" for="schedule-mode">Публиковать после генерации</label>
                        </div>
                        <span :class="{ 'mode-switch__label--active': form.mode === 'GENERATE_AND_PUBLISH' }">
                          Генерация + публикация
                        </span>
                      </div>
                    </div>
                    <div class="schedule-field">
                      <label class="left-headers form-label mb-0">Каждые N часов</label>
                      <input type="number" class="form-control form-control-sm" min="1" max="720" v-model.number="form.intervalHours" />
                    </div>
                  </div>
                  <div v-if="globalCmakerDefs.length" class="schedule-global">
                    <div class="cmaker-group__head">
                      <span class="cmaker-group__title">Глобальные параметры</span>
                      <span class="cmaker-group__sub">Бюджет и лимиты на один run</span>
                    </div>
                    <SettingDefForm :defs="globalCmakerDefs" v-model="cmakerSettings" />
                  </div>
                </div>
              </section>

              <!-- Step 2 — RSS feeds -->
              <section class="schedule-step">
                <div class="step-head">
                  <span class="step-num">2</span>
                  <div class="step-head__text">
                    <div class="step-title">Источники (RSS)</div>
                    <div class="step-sub">Выбрано: {{ selectedRssIds.length }} / {{ filteredRssChannels.length }}</div>
                  </div>
                </div>
                <div class="step-body">
                  <div class="d-flex gap-2 align-items-center mb-2">
                    <select class="form-select form-select-sm rss-category-select" v-model="rssCategoryFilter">
                      <option value="">Все категории</option>
                      <option v-for="c in rssCategories" :key="c" :value="c">{{ c }}</option>
                    </select>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      :disabled="!rssCategoryFilter"
                      @click="selectAllRss"
                    >
                      Выбрать все
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary"
                      :disabled="!selectedRssIds.length"
                      @click="deselectAllRss"
                    >
                      Снять все
                    </button>
                  </div>
                  <div class="rss-list">
                    <div v-for="ch in filteredRssChannels" :key="ch.id" class="rss-item">
                      <div class="form-check form-switch rss-switch">
                        <input
                          :id="`rss-${ch.id}`"
                          class="form-check-input"
                          type="checkbox"
                          :checked="selectedRssIds.includes(ch.id)"
                          @change="toggleRss(ch.id, $event.target.checked)"
                        />
                        <label class="form-check-label" :for="`rss-${ch.id}`">
                          <span class="fw-semibold">{{ ch.name }}</span>
                          <span class="text-muted small ms-2">{{ ch.url }}</span>
                        </label>
                      </div>
                    </div>
                    <div v-if="!filteredRssChannels.length" class="text-muted small">
                      Нет доступных enabled-каналов{{ rssCategoryFilter ? ` в категории ${rssCategoryFilter}` : '' }}.
                    </div>
                  </div>
                </div>
              </section>

              <!-- Step 3 — cmaker run config -->
              <section class="schedule-step">
                <div class="step-head">
                  <span class="step-num">3</span>
                  <div class="step-head__text">
                    <div class="step-title">Параметры генерации</div>
                  </div>
                </div>
                <div class="step-body">
                  <div v-if="!cmakerGroups.length" class="text-muted small">
                    Нет настроек для отображения.
                  </div>
                  <div v-else class="cmaker-groups">
                    <div
                      v-for="(g, idx) in cmakerGroups"
                      :key="g.key"
                      class="cmaker-group"
                      :class="{ 'cmaker-group--divided': idx > 0 }"
                    >
                      <div class="cmaker-group__head">
                        <span class="cmaker-group__title">{{ g.title }}</span>
                        <span v-if="g.sub" class="cmaker-group__sub">{{ g.sub }}</span>
                      </div>
                      <SettingDefForm :defs="g.defs" v-model="cmakerSettings" :columns="4" />
                    </div>
                  </div>
                  <div v-if="qaWriterConflict" class="alert alert-warning mt-2 mb-0">
                    Модель редактора/QA совпадает с моделью автора статьи (article_writer), а включён режим
                    «Модель редактора/QA должна отличаться». cmaker отклонит запуск — выберите для editor_qa
                    другую модель или отключите must_differ_from_writer.
                  </div>
                  <div v-if="wordCountConflict" class="alert alert-warning mt-2 mb-0">
                    Мин. длина исходной статьи больше максимальной (base_article). cmaker отклонит запуск —
                    сделайте min_word_count ≤ max_word_count.
                  </div>
                </div>
              </section>

              <!-- Manual run + link to history (edit only) -->
              <section v-if="isEdit" class="schedule-step">
                <div class="step-head">
                  <span class="step-num step-num--muted"><i class="bi bi-play-circle"></i></span>
                  <div class="step-head__text">
                    <div class="step-title">Запуск</div>
                    <div class="step-sub">Ручной запуск и переход к истории запусков</div>
                  </div>
                </div>
                <div class="step-body">
                  <div class="d-flex gap-2 align-items-center flex-wrap">
                    <button
                      type="button"
                      class="btn btn-outline-primary btn-sm"
                      :disabled="!schedule || running"
                      @click="onRunNow"
                    >
                      <span v-if="running" class="spinner-border spinner-border-sm me-1"></span>
                      Запустить сейчас
                    </button>
                    <router-link :to="`/cmaker/runs?streamId=${streamData.id}`" class="btn btn-outline-secondary btn-sm">
                      <i class="bi bi-clock-history me-1"></i>
                      История запусков
                    </router-link>
                    <button
                      type="button"
                      class="btn btn-outline-danger btn-sm"
                      v-if="schedule"
                      :disabled="saving"
                      @click="onDeleteSchedule"
                    >
                      Удалить расписание
                    </button>
                    <span v-if="runNowMessage" class="text-muted small ms-2">{{ runNowMessage }}</span>
                  </div>
                  <div v-if="!scheduleCanSave" class="alert alert-warning mt-2 mb-0 py-2 px-3 small">
                    Чтобы сохранить/включить расписание: привяжите проект сайта, задайте интервал от 1 до 720 часов и
                    выберите хотя бы один RSS-источник.
                  </div>
                </div>
              </section>
            </div>

            <!-- Ручная генерация -->
            <div v-show="activeTab === 'manual'" class="tab-pane-body">
              <AiModelFields
                v-model:ai-provider="local.aiProvider"
                v-model:ai-model="local.aiModel"
                v-model:settings="local.settings"
                :required="aiRequired"
                :ai-providers="aiProviders"
                :ai-models="aiModels"
                :reasoning-setting="reasoningSetting"
              />
              <GenerationSettingsGrid
                :grouped-generation="groupedGeneration"
                v-model="local.settings"
                :empty-text="generationEmptyText"
              />
            </div>
          </template>

          <!-- НЕ-WordPress: одиночная панель ручной генерации -->
          <template v-else>
            <GenerationSettingsGrid
              :grouped-generation="groupedGeneration"
              v-model="local.settings"
              :empty-text="generationEmptyText"
            />
          </template>
        </div>
      </div>

      <div class="page-footer">
        <div v-if="saveError" class="text-danger small me-auto">{{ saveError }}</div>
        <button type="button" class="btn btn-primary btn-sm" @click="onSave" :disabled="!canSave || saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
          {{ isEdit ? 'Сохранить' : 'Создать' }}
        </button>
        <button type="button" class="btn btn-secondary btn-sm" @click="goBack">Отмена</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import CredentialModal from './CredentialModal.vue'
import AiModelFields from './AiModelFields.vue'
import GenerationSettingsGrid from './GenerationSettingsGrid.vue'
import SettingDefForm from './SettingDefForm.vue'
import {
  getSchedule,
  upsertSchedule,
  deleteSchedule,
  runNow,
} from '@/services/streamScheduleService'
import { listEnabledChannels, listCategories } from '@/services/rssChannelService'
import {
  getSiteProject,
  getSiteProjects,
  getSiteProjectIdentity,
} from '@/services/wordpressConceptService'
import { getCredentialsByType } from '@/services/credentialService'

const route = useRoute()
const router = useRouter()

const isEdit = computed(() => !!route.params.id)

// Initial tab: schedule gear → auto; edit gear → manual; create → auto (manual
// "по сути не используется" for WordPress per product decision).
const activeTab = ref(route.name === 'StreamEdit' ? 'manual' : 'auto')

function goBack() {
  if (window.history.length > 1) {
    router.back()
  } else {
    router.push('/streams')
  }
}

// Edit-mode state
const streamData = ref(null)
const loadingStream = ref(false)
const loadError = ref('')

// Create-mode state
const localType = ref('')
const types = ref([])
const ALLOWED_STREAM_TYPES = ['WORDPRESS', 'TELEGRAM', 'YANDEX_DZEN']
const isAllowedStreamType = t => ALLOWED_STREAM_TYPES.includes(String(t || ''))

// Unified stream type (editable in create, fixed in edit)
const streamType = computed(() => isEdit.value ? streamData.value?.type : localType.value)
const streamTypeBadgeClass = computed(() => {
  const type = String(streamType.value || '').toUpperCase()
  if (type === 'WORDPRESS') return 'stream-type-badge--wordpress'
  if (type === 'TELEGRAM') return 'stream-type-badge--telegram'
  if (type === 'YANDEX_DZEN') return 'stream-type-badge--dzen'
  return ''
})

// Type-specific flags
const isTgPublishing = computed(() => ['TELEGRAM', 'YANDEX_DZEN'].includes(streamType.value))
const isWpPublishing = computed(() => streamType.value === 'WORDPRESS')
const credentialType = computed(() => (isWpPublishing.value ? 'WORDPRESS' : 'TELEGRAM'))

// Credential modal
const showCredentialModal = ref(false)

function onCredentialSelected(cred) {
  if (isWpPublishing.value) local.settings.wordpressCredential = cred
  else local.settings.telegramCredential = cred
  showCredentialModal.value = false
}

function clearCredential() {
  if (isWpPublishing.value) delete local.settings.wordpressCredential
  else delete local.settings.telegramCredential
}

// Original settings (preserved for edit mode to restore values on provider change)
const original = {}

// Form state
const local = reactive({
  name: '',
  description: '',
  aiProvider: '',
  aiModel: '',
  settings: {}
})

// Settings lists
const aiProviders = ref([])
const aiModels = ref([])
const defs = ref([])
const genDefs = ref([])
const pubDefs = ref([])
const saving = ref(false)
const saveError = ref('')

// ─── Auto-generation (cmaker + schedule) state ──────────────────────────────
const schedule = ref(null)
const rssChannels = ref([])
const rssCategories = ref([])
const rssCategoryFilter = ref('')
const cmakerDefs = ref([])
const cmakerSettings = ref({})
const selectedRssIds = ref([])
const running = ref(false)
const runNowMessage = ref('')
const wpResourcesLoaded = ref(false)

// Site projects
const siteProjects = ref([])
const siteProjectsLoading = ref(false)
const siteProjectsError = ref('')
const selectedSiteProjectId = ref('')

const form = reactive({
  enabled: true,
  mode: 'GENERATE_ONLY',
  intervalHours: 6,
  timezone: 'Europe/Moscow',
  overlapPolicy: 'SKIP',
})

// ─── Sorting helpers ───────────────────────────────────────────────────────────
const order = { DROP_DOWN: 0, MULTI_SELECT: 1, SLIDER: 2, CHECKBOX: 3, TEXT_BOX: 4, TEXT_AREA: 5 }

const isWritingLanguageSetting = def => {
  const desc = String(def?.description || '').toLowerCase()
  const key = String(def?.settingKey || '').toLowerCase()
  return desc.includes('язык написания') || key === 'language' || key.endsWith('language')
}

const sortedGeneration = computed(() =>
  genDefs.value
    .map((def, index) => ({ def, index }))
    .sort((l, r) => {
      const diff = (order[l.def.settingType] ?? 999) - (order[r.def.settingType] ?? 999)
      if (diff !== 0) return diff
      if (l.def.settingType === 'DROP_DOWN') {
        const lLang = isWritingLanguageSetting(l.def)
        const rLang = isWritingLanguageSetting(r.def)
        if (lLang !== rLang) return lLang ? -1 : 1
      }
      return l.index - r.index
    })
    .map(item => item.def)
)

const reasoningSetting = computed(() =>
  sortedGeneration.value.find(d => String(d?.settingKey || '').toLowerCase() === 'reasoning') || null
)

const rightPanelGeneration = computed(() =>
  sortedGeneration.value.filter(d => String(d?.settingKey || '').toLowerCase() !== 'reasoning')
)

const groupedGeneration = computed(() => {
  const groups = []
  const byType = new Map()
  rightPanelGeneration.value.forEach(def => {
    const type = def.settingType || 'UNKNOWN'
    if (!byType.has(type)) {
      const group = { type, items: [] }
      byType.set(type, group)
      groups.push(group)
    }
    byType.get(type).items.push(def)
  })
  return groups
})

const generationEmptyText = computed(() => {
  if (isEdit.value) return 'Настройки генерации скрыты.'
  // For WordPress the AI/model selectors live in this same manual tab, so the
  // "слева" hint does not apply.
  if (isWpPublishing.value) return 'Выберите нейросеть и модель для ручной генерации.'
  return 'Задайте все обязательные настройки в левой части.'
})

// ─── cmaker grouping ───────────────────────────────────────────────────────────
const CMAKER_GROUPS = [
  { key: 'preselection', title: 'Предотбор (preselection)', sub: 'Сбор и отбор публикаций, эмбеддинги' },
  { key: 'rerank', title: 'Реранжирование (rerank)', sub: 'Финальный отбор материалов' },
  { key: 'base_article', title: 'Базовая статья (base_article)', sub: 'Требования к исходному материалу' },
  { key: 'queries', title: 'Поисковые запросы (queries)', sub: 'LLM для генерации запросов' },
  { key: 'editorial_brief_draft', title: 'Бриф — черновик (editorial_brief_draft)', sub: 'Гейт и LLM черновика брифа' },
  { key: 'editorial_brief_final', title: 'Бриф — финал (editorial_brief_final)', sub: 'Гейт и LLM финального брифа' },
  { key: 'article_writer', title: 'Автор статьи (article_writer)', sub: 'LLM, пишущий текст' },
  { key: 'editor_qa', title: 'Редактор (editor_qa)', sub: 'LLM проверки финального текста' },
  { key: 'seo_metadata', title: 'SEO-метаданные (seo_metadata)', sub: 'title / description / slug' },
  { key: 'image_selection', title: 'Подбор изображения (image_selection)', sub: 'Vision-LLM и провайдеры' },
]

function stageOf(settingKey) {
  const parts = String(settingKey || '').split('.')
  return parts.length >= 2 ? parts[1] : ''
}

// Keys never shown in the form. seo_metadata.enabled is always on (forced true
// on load), so it is hidden rather than rendered as a toggle.
const HIDDEN_CMAKER_KEYS = new Set(['cmaker.seo_metadata.enabled'])
const FORCED_TRUE_CMAKER_KEYS = ['cmaker.seo_metadata.enabled']

const cmakerGroups = computed(() => {
  const defsList = (cmakerDefs.value || []).filter(d => !HIDDEN_CMAKER_KEYS.has(d.settingKey))
  const known = new Set([...CMAKER_GROUPS.map(g => g.key), 'global'])
  const groups = CMAKER_GROUPS
    .map(g => ({ ...g, defs: defsList.filter(d => stageOf(d.settingKey) === g.key) }))
    .filter(g => g.defs.length)
  const otherDefs = defsList.filter(d => !known.has(stageOf(d.settingKey)))
  if (otherDefs.length) {
    groups.push({ key: '__other', title: 'Прочее', sub: '', defs: otherDefs })
  }
  return groups
})

const globalCmakerDefs = computed(() =>
  (cmakerDefs.value || []).filter(def => stageOf(def.settingKey) === 'global')
)

function toBoolLike(v) {
  if (typeof v === 'boolean') return v
  if (typeof v === 'number') return v === 1
  if (typeof v === 'string') return ['true', '1', 'yes', 'on'].includes(v.trim().toLowerCase())
  return Boolean(v)
}

const qaWriterConflict = computed(() => {
  const s = cmakerSettings.value || {}
  // No conflict when editor_qa is disabled — cmaker skips the must_differ check then.
  const editorQaEnabled = s['cmaker.editor_qa.enabled']
  if (editorQaEnabled !== undefined && !toBoolLike(editorQaEnabled)) return false
  if (!toBoolLike(s['cmaker.editor_qa.must_differ_from_writer'])) return false
  const qaModel = s['cmaker.editor_qa.model']
  const writerModel = s['cmaker.article_writer.model']
  return Boolean(qaModel) && Boolean(writerModel) && qaModel === writerModel
})

const wordCountConflict = computed(() => {
  const s = cmakerSettings.value || {}
  const min = Number(s['cmaker.base_article.min_word_count'])
  const max = Number(s['cmaker.base_article.max_word_count'])
  return Number.isFinite(min) && Number.isFinite(max) && min > max
})

const scheduleCanSave = computed(() =>
  isWpPublishing.value
    && !!normalizeString(selectedSiteProjectId.value)
    && Number.isFinite(form.intervalHours) && form.intervalHours >= 1 && form.intervalHours <= 720
    && !!(form.timezone && form.timezone.trim())
    && selectedRssIds.value.length > 0
    && !qaWriterConflict.value
    && !wordCountConflict.value
)

// ─── Validation ────────────────────────────────────────────────────────────────
// WordPress streams are created primarily for automatic generation, so AI
// provider/model (manual-generation fields) are not required to create one.
const aiRequired = computed(() => !isEdit.value && !isWpPublishing.value)

const canSave = computed(() => {
  if (isEdit.value) return local.name.trim()
  if (isWpPublishing.value) return local.name.trim() && localType.value
  return local.name.trim() && localType.value && local.aiProvider && local.aiModel
})

// ─── Value normalization ───────────────────────────────────────────────────────
function toArrayValue(value) {
  if (Array.isArray(value)) return value
  if (value === null || value === undefined || value === '') return []
  return [value]
}

function toBooleanValue(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase()
    if (['true', '1', 'yes', 'on'].includes(v)) return true
    if (['false', '0', 'no', 'off', ''].includes(v)) return false
  }
  return Boolean(value)
}

function normalizeSettingValue(def, value) {
  if (def.settingType === 'MULTI_SELECT') return toArrayValue(value)
  if (def.settingType === 'CHECKBOX') return toBooleanValue(value)
  return value
}

function normalizeSettingsByType(settings, settingDefs) {
  const normalized = { ...settings }
  settingDefs.forEach(def => {
    normalized[def.settingKey] = normalizeSettingValue(def, normalized[def.settingKey])
  })
  return normalized
}

function normalizeString(value) {
  return String(value || '').trim()
}

// ─── Settings loader ───────────────────────────────────────────────────────────
function pickDef(arr) {
  const specific = arr.find(d =>
    Array.isArray(d.dependencies) &&
    d.dependencies.some(dep =>
      dep.settingKey === 'model' &&
      Array.isArray(dep.values) &&
      dep.values.includes(local.aiModel)
    )
  )
  if (specific) return specific
  return arr.find(d => !Array.isArray(d.dependencies) || d.dependencies.length === 0)
}

function preserveCredentials() {
  const p = {}
  if (isTgPublishing.value) {
    if (local.settings?.telegramCredential) p.telegramCredential = local.settings.telegramCredential
    if (local.settings?.chatId) p.chatId = local.settings.chatId
  }
  if (isWpPublishing.value) {
    if (local.settings?.wordpressCredential) p.wordpressCredential = local.settings.wordpressCredential
    if (local.settings?.siteUrl) p.siteUrl = local.settings.siteUrl
  }
  return p
}

async function loadDefs(overrideModel = false) {
  const type = streamType.value
  if (!type || !local.aiProvider) return

  const preserved = preserveCredentials()

  try {
    const { data } = await axios.get('/api/stream-settings/by-type', {
      params: { streamType: type, aiName: local.aiProvider }
    })
    defs.value = data

    const modelDef = data.find(d => d.settingKey === 'model')
    aiModels.value = modelDef?.metadata?.options || []

    let effectiveModel = local.aiModel
    if (overrideModel && modelDef) {
      effectiveModel = modelDef.defaultValue || ''
      local.aiModel = effectiveModel
    }

    genDefs.value = []
    pubDefs.value = []
    const byKey = {}
    data.forEach(d => {
      if (d.settingKey !== 'model') (byKey[d.settingKey] ||= []).push(d)
    })

    const nextSettings = {
      ...preserved,
      aiProvider: local.aiProvider,
      model: effectiveModel || local.aiModel || ''
    }

    Object.entries(byKey).forEach(([key, arr]) => {
      const def = pickDef(arr)
      if (!def) return

      const isOrig =
        isEdit.value &&
        local.aiProvider === original.aiProvider &&
        (effectiveModel || local.aiModel) === original.model &&
        original[key] !== undefined

      let value
      if (isOrig) value = original[key]
      else if (def.defaultValue != null) value = def.defaultValue
      else if (local.settings[key] !== undefined) value = local.settings[key]
      else value = ''

      nextSettings[key] = normalizeSettingValue(def, value)

      if (def.category === 'GENERATION') genDefs.value.push(def)
      else pubDefs.value.push(def)
    })

    local.settings = nextSettings
  } catch (e) {
    console.error('Не удалось загрузить описания настроек:', e)
  }
}

// ─── Site projects ─────────────────────────────────────────────────────────────
function resolveSiteProjectIdFromStream(payload) {
  return normalizeString(
    payload?.siteProjectId
    || payload?.site_project_id
    || payload?.siteProject?.id
    || payload?.site_project?.id
  )
}

function normalizeSiteProject(item) {
  const src = item && typeof item === 'object' ? item : {}
  const id = normalizeString(src.id)
  if (!id) return null
  const topic = normalizeString(src.topicName || src.topic_name || src.name || src.title)
  const selectedDomainId = normalizeString(src.selectedDomainId || src.selected_domain_id)
  const domains = Array.isArray(src.domains) ? src.domains : []
  const selectedDomain = domains.find(d => normalizeString(d?.id) === selectedDomainId)
  const domain = normalizeString(
    src.selectedDomainName
    || src.selected_domain_name
    || src.selectedDomain?.domainName
    || src.selected_domain?.domain_name
    || selectedDomain?.domainName
    || selectedDomain?.domain_name
    || src.domain
  )
  const label = topic && domain ? `${domain} - ${topic}` : (domain || topic || id)
  return { id, label, domain }
}

async function loadSiteProjects() {
  const list = await getSiteProjects()
  return Promise.all(list.map(async item => {
    const normalized = normalizeSiteProject(item)
    if (!normalized || normalized.domain) return normalized
    try {
      return normalizeSiteProject(await getSiteProject(normalized.id)) || normalized
    } catch {
      return normalized
    }
  })).then(items => items.filter(Boolean))
}

// On manual SiteProject selection: auto-fill name ← domain, description ←
// siteTitle, URL ← https://<domain>, WordPress user ← credential whose label
// matches the domain. Fired only on user @change, so the initial edit load does
// not clobber existing values.
async function onSiteProjectChange() {
  const id = normalizeString(selectedSiteProjectId.value)
  if (!id) return
  try {
    const proj = await getSiteProject(id)
    const domain = normalizeSiteProject(proj)?.domain || ''

    let siteTitle = ''
    try {
      const identity = await getSiteProjectIdentity(id)
      siteTitle = normalizeString(identity?.siteTitle)
    } catch {
      // identity may not exist yet — leave description untouched
    }

    if (domain) {
      local.name = domain
      local.settings.siteUrl = `https://${domain}`
      try {
        const creds = await getCredentialsByType('WORDPRESS')
        const match = (creds || []).find(c =>
          normalizeString(c?.label).toLowerCase() === domain.toLowerCase()
        )
        if (match) local.settings.wordpressCredential = match
      } catch (e) {
        console.error('Не удалось подобрать учётную запись WordPress:', e)
      }
    }
    if (siteTitle) local.description = siteTitle
  } catch (e) {
    console.error('Не удалось загрузить проект сайта:', e)
  }
}

function extractErrorMessage(error) {
  return error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Unknown error'
}

// ─── WordPress auto-generation resources ────────────────────────────────────────
// Group cmaker defs by setting key. Model keys (e.g. *.model, *.vision_model)
// have two rows — one per provider — distinguished by their `dependencies`.
function cmakerDefsByKey() {
  const byKey = new Map()
  for (const def of (cmakerDefs.value || [])) {
    if (!byKey.has(def.settingKey)) byKey.set(def.settingKey, [])
    byKey.get(def.settingKey).push(def)
  }
  return byKey
}

// Pick the row whose dependencies match the current settings (e.g. the model row
// for the currently selected provider), else the dependency-free row. Mirrors
// SettingDefForm.pickByDependencies.
function pickCmakerDef(arr, settings) {
  const specific = arr.find(d =>
    Array.isArray(d.dependencies) && d.dependencies.length > 0 &&
    d.dependencies.every(dep => Array.isArray(dep.values) && dep.values.includes(settings[dep.settingKey]))
  )
  if (specific) return specific
  return arr.find(d => !Array.isArray(d.dependencies) || d.dependencies.length === 0) || arr[0]
}

// A valid seed value for a def: its defaultValue when it is a real option, else
// the first available option (options are resolved from the pricing snapshot at
// read-time for model dropdowns, so a stale default may not be present).
function defaultForDef(def) {
  const options = def?.metadata?.options
  const dv = def?.defaultValue
  if (Array.isArray(options) && options.length) {
    return (dv !== undefined && options.includes(dv)) ? dv : options[0]
  }
  return dv
}

function seedCmakerDefaults() {
  const seeded = { ...cmakerSettings.value }
  const byKey = cmakerDefsByKey()
  // Pass 1: dependency-free defs (providers, sliders, toggles) so provider
  // values exist before we resolve provider-dependent model defs.
  for (const [key, arr] of byKey) {
    if (seeded[key] !== undefined) continue
    const plain = arr.find(d => !Array.isArray(d.dependencies) || d.dependencies.length === 0)
    if (plain) seeded[key] = defaultForDef(plain)
  }
  // Pass 2: dependency-based defs (model picked by its stage provider).
  for (const [key, arr] of byKey) {
    if (seeded[key] !== undefined) continue
    seeded[key] = defaultForDef(pickCmakerDef(arr, seeded))
  }
  cmakerSettings.value = seeded
}

// Keep dropdown values valid for the currently picked def — e.g. after the user
// switches a stage provider, reset its model to that provider's default instead
// of leaving a value that is not in the new option list (blank select).
function reconcileCmakerOptions() {
  const byKey = cmakerDefsByKey()
  const next = { ...cmakerSettings.value }
  let changed = false
  for (const [key, arr] of byKey) {
    const def = pickCmakerDef(arr, next)
    const options = def?.metadata?.options
    if (Array.isArray(options) && options.length && !options.includes(next[key])) {
      next[key] = defaultForDef(def)
      changed = true
    }
  }
  if (changed) cmakerSettings.value = next
}

async function loadWpResources() {
  if (wpResourcesLoaded.value) return
  wpResourcesLoaded.value = true
  siteProjectsLoading.value = true
  siteProjectsError.value = ''
  try {
    const aiName = local.aiProvider || streamData.value?.settings?.aiProvider || 'CHAT_GPT'
    const [categories, channels, cDefs, projects] = await Promise.all([
      listCategories(),
      listEnabledChannels(),
      axios.get('/api/stream-settings/by-type', {
        params: { streamType: 'WORDPRESS', aiName, category: 'CMAKER_RUN' },
      }).then(r => r.data),
      loadSiteProjects().catch(error => {
        siteProjectsError.value = extractErrorMessage(error)
        return []
      }),
    ])
    rssCategories.value = categories
    rssChannels.value = channels
    cmakerDefs.value = Array.isArray(cDefs) ? cDefs : []
    siteProjects.value = projects
    seedCmakerDefaults()

    if (isEdit.value && streamData.value) {
      // Pull cmaker.* values from stream.settings, keeping only keys still present
      // in the current definitions (stale keys are dropped).
      const settings = streamData.value.settings || {}
      const defKeys = new Set(cmakerDefs.value.map(d => d.settingKey))
      const next = {}
      Object.keys(settings).forEach(k => {
        if (k.startsWith('cmaker.') && (defKeys.size === 0 || defKeys.has(k))) next[k] = settings[k]
      })
      cmakerSettings.value = { ...cmakerSettings.value, ...next }
      selectedSiteProjectId.value = resolveSiteProjectIdFromStream(streamData.value)

      const sched = await getSchedule(streamData.value.id).catch(() => null)
      schedule.value = sched
      if (sched) {
        form.enabled = sched.enabled
        form.mode = sched.mode
        form.intervalHours = sched.intervalHours
        form.timezone = sched.timezone || 'Europe/Moscow'
        form.overlapPolicy = sched.overlapPolicy || 'SKIP'
        selectedRssIds.value = (sched.feeds || []).map(f => f.rssChannelId)
      }
    }

    // Hidden settings that must always be on, regardless of stored value.
    const forcedCmaker = {}
    FORCED_TRUE_CMAKER_KEYS.forEach(k => { forcedCmaker[k] = true })
    cmakerSettings.value = { ...cmakerSettings.value, ...forcedCmaker }
  } catch (e) {
    console.error('Не удалось загрузить ресурсы WordPress:', e)
  } finally {
    siteProjectsLoading.value = false
  }
}

// ─── RSS helpers ────────────────────────────────────────────────────────────────
const filteredRssChannels = computed(() => {
  if (!rssCategoryFilter.value) return rssChannels.value
  return rssChannels.value.filter(c => c.category === rssCategoryFilter.value)
})

function toggleRss(id, on) {
  const set = new Set(selectedRssIds.value)
  if (on) set.add(id); else set.delete(id)
  selectedRssIds.value = Array.from(set)
}

function selectAllRss() {
  if (!rssCategoryFilter.value) return
  const set = new Set(selectedRssIds.value)
  filteredRssChannels.value.forEach(ch => set.add(ch.id))
  selectedRssIds.value = Array.from(set)
}

function deselectAllRss() {
  if (!selectedRssIds.value.length) return
  if (!rssCategoryFilter.value) {
    selectedRssIds.value = []
    return
  }
  const visible = new Set(filteredRssChannels.value.map(ch => ch.id))
  selectedRssIds.value = selectedRssIds.value.filter(id => !visible.has(id))
}

// ─── Merged settings (the cmaker-wipe fix) ──────────────────────────────────────
// A single save writes the complete settings object: existing keys, minus stale
// cmaker.*, plus the normalized manual settings, plus the current cmaker.* form
// values. This prevents the manual save from ever dropping cmaker.* (and vice
// versa), since both live in one object now.
function buildMergedSettings(normalizedManual) {
  const base = isEdit.value ? { ...(streamData.value?.settings || {}) } : {}
  // Drop every cmaker.* key: from the stored settings (stale values) and from
  // the manual portion (the by-type endpoint may surface CMAKER_RUN defs whose
  // defaults must not leak in). The auto tab's cmakerSettings is the only source.
  Object.keys(base).forEach(k => {
    if (k.startsWith('cmaker.')) delete base[k]
  })
  Object.entries(normalizedManual).forEach(([k, v]) => {
    if (!k.startsWith('cmaker.')) base[k] = v
  })
  Object.entries(cmakerSettings.value).forEach(([k, v]) => {
    if (k.startsWith('cmaker.')) base[k] = v
  })
  return base
}

function buildNormalizedManual() {
  const normalized = normalizeSettingsByType(local.settings, defs.value)
  normalized.aiProvider = local.aiProvider
  normalized.model = local.aiModel
  return normalized
}

// ─── Watchers ──────────────────────────────────────────────────────────────────
watch(
  [streamType, () => local.aiProvider],
  async ([t, ai]) => {
    if (!t || !ai) {
      aiModels.value = []
      genDefs.value = []
      pubDefs.value = []
      if (!isEdit.value) {
        local.aiModel = ''
        local.settings = {}
      }
      return
    }
    await loadDefs(true)
  }
)

watch(() => local.aiModel, async val => {
  local.settings.model = val
  await loadDefs(false)
})

// Load WordPress-only resources (cmaker defs, RSS, site projects, schedule) once
// the type is WordPress (covers both create-time type selection and edit load).
watch(streamType, async t => {
  if (t === 'WORDPRESS') await loadWpResources()
})

// When a cmaker stage provider changes, re-validate dependent dropdowns (models)
// so they don't end up showing a value that isn't in the new provider's options.
watch(cmakerSettings, reconcileCmakerOptions)

// ─── Save ──────────────────────────────────────────────────────────────────────
async function onSave() {
  if (!canSave.value) {
    alert(isEdit.value
      ? 'Заполните все обязательные поля.'
      : isWpPublishing.value
        ? 'Заполните обязательные поля: Название и Тип стрима.'
        : 'Заполните все обязательные поля: Название, Тип стрима, Нейросеть и Модель.')
    return
  }
  saving.value = true
  saveError.value = ''
  try {
    const mergedSettings = buildMergedSettings(buildNormalizedManual())

    let streamId
    if (isEdit.value) {
      const credId = isWpPublishing.value
        ? local.settings?.wordpressCredential?.id
        : local.settings?.telegramCredential?.id

      await axios.put(`/api/streams/${streamData.value.id}`, {
        ...streamData.value,
        name: local.name,
        description: local.description,
        settings: mergedSettings,
        credential: credId ? { id: credId } : null,
      })
      streamId = streamData.value.id
    } else {
      const { data } = await axios.post('/api/streams', {
        name: local.name,
        description: local.description,
        type: localType.value,
        settings: mergedSettings,
      })
      streamId = data?.id
    }

    // WordPress extras: bind SiteProject, then upsert the schedule when valid.
    if (isWpPublishing.value && streamId) {
      const siteProjectId = normalizeString(selectedSiteProjectId.value)
      if (siteProjectId) {
        try {
          await axios.put(`/api/streams/${streamId}/site-project`, { siteProjectId })
        } catch (e) {
          console.error('Не удалось привязать проект сайта:', e)
        }
      }
      if (scheduleCanSave.value) {
        try {
          await upsertSchedule(streamId, {
            enabled: form.enabled,
            mode: form.mode,
            intervalHours: form.intervalHours,
            timezone: form.timezone,
            overlapPolicy: form.overlapPolicy,
            rssChannelIds: selectedRssIds.value,
          })
        } catch (e) {
          console.error('Не удалось сохранить расписание:', e)
          saveError.value = e?.response?.data?.message
            || 'Стрим сохранён, но расписание не удалось сохранить.'
          saving.value = false
          return
        }
      }
    }

    goBack()
  } catch (e) {
    console.error(e)
    saveError.value = 'Ошибка при сохранении.'
  } finally {
    saving.value = false
  }
}

// ─── Run-now / delete schedule (edit only) ──────────────────────────────────────
async function onRunNow() {
  if (!isEdit.value) return
  running.value = true
  runNowMessage.value = ''
  try {
    const jobId = await runNow(streamData.value.id, buildMergedSettings(buildNormalizedManual()))
    runNowMessage.value = `Запущено: run #${jobId}`
  } catch (e) {
    console.error(e)
    runNowMessage.value = e?.response?.data?.message || 'Не удалось запустить.'
  } finally {
    running.value = false
  }
}

async function onDeleteSchedule() {
  if (!isEdit.value) return
  if (!confirm('Удалить расписание для этого стрима?')) return
  saving.value = true
  saveError.value = ''
  try {
    await deleteSchedule(streamData.value.id)
    schedule.value = null
  } catch (e) {
    console.error(e)
    saveError.value = 'Не удалось удалить расписание.'
  } finally {
    saving.value = false
  }
}

// ─── Init ──────────────────────────────────────────────────────────────────────
async function fetchTypes() {
  const { data } = await axios.get('/api/streams/types')
  const fetched = Array.isArray(data) ? data : []
  const filtered = fetched.filter(isAllowedStreamType)
  types.value = filtered.length ? filtered : [...ALLOWED_STREAM_TYPES]
  if (!isAllowedStreamType(localType.value)) localType.value = ''
}

onMounted(async () => {
  if (isEdit.value) {
    loadingStream.value = true
    try {
      const [streamResp, providersResp] = await Promise.all([
        axios.get(`/api/streams/${route.params.id}`),
        axios.get('/api/streams/ai-providers')
      ])
      const data = streamResp.data
      streamData.value = data
      aiProviders.value = providersResp.data

      Object.keys(original).forEach(k => delete original[k])
      Object.assign(original, data.settings || {})

      local.name = data.name
      local.description = data.description
      local.aiProvider = original.aiProvider || ''
      local.aiModel = original.model || ''
      local.settings = { ...original }

      if (data.credential?.id) {
        if (isWpPublishing.value) local.settings.wordpressCredential = data.credential
        else local.settings.telegramCredential = data.credential
      }

      await loadDefs(false)
      if (isWpPublishing.value) await loadWpResources()
    } catch (e) {
      console.error('Не удалось загрузить стрим:', e)
      loadError.value = 'Не удалось загрузить данные стрима.'
    } finally {
      loadingStream.value = false
    }
  } else {
    await Promise.all([
      fetchTypes(),
      axios.get('/api/streams/ai-providers').then(r => { aiProviders.value = r.data })
    ])
  }
})
</script>

<style scoped>
.stream-form-page {
  display: flex;
  flex-direction: column;
  height: calc(var(--app-vh, 1vh) * 100 - 2px);
  padding: 8px;
  background:
    radial-gradient(circle at 2% 3%, rgba(66, 153, 225, 0.2), transparent 36%),
    radial-gradient(circle at 98% 2%, rgba(56, 189, 248, 0.18), transparent 32%),
    #f3f7ff;
  overflow: hidden;
  box-sizing: border-box;
}

.page-hero {
  flex: 0 0 auto;
  padding: 12px 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #10305f 0%, #1b4f93 60%, #1e67b7 100%);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 18px 40px rgba(16, 48, 95, 0.22);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.page-hero__title {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.page-hero__subtitle {
  color: rgba(232, 242, 255, 0.92);
  font-size: 13px;
}

.page-hero__back {
  color: #103f77;
  border-color: #cfe2ff;
  font-weight: 700;
  white-space: nowrap;
}

.form-card {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 16px;
  border: 1px solid #dfe7f5;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
}

.load-state {
  align-items: center;
  justify-content: center;
  flex-direction: row;
  padding: 40px;
}

.load-state--col {
  flex-direction: column !important;
}

.split-body {
  display: flex;
  flex: 1 1 auto;
  overflow: hidden;
  min-height: 0;
}

.left-panel {
  flex: 0 0 38%;
  padding: 12px;
  overflow-y: auto;
  min-height: 0;
}

.left-panel form {
  min-height: 100%;
  background: #f8fbff;
  border: 1px solid #e1eaf8;
  border-radius: 12px;
  padding: 12px;
}

.right-panel {
  flex: 1 1 auto;
  padding: 16px 18px 18px;
  overflow-y: auto;
  min-height: 0;
  position: relative;
  border-left: 1px solid #dce6f8;
  box-shadow: inset 10px 0 14px -14px rgba(31, 61, 138, 0.45);
}

.right-panel::before {
  content: '';
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 1px;
  background: linear-gradient(to bottom, transparent, #d0dcf2 12%, #d0dcf2 88%, transparent);
  pointer-events: none;
}

.left-panel::-webkit-scrollbar,
.right-panel::-webkit-scrollbar { width: 8px; }

.left-panel::-webkit-scrollbar-thumb,
.right-panel::-webkit-scrollbar-thumb { border-radius: 8px; background: #d1ddef; }

.left-panel::-webkit-scrollbar-track,
.right-panel::-webkit-scrollbar-track { background: transparent; }

.left-panel form > .mb-2,
.left-panel form > div > .mb-2 { margin-bottom: 10px !important; }

.left-panel .form-control,
.left-panel .form-select { border-radius: 10px; }

.left-panel .form-control:focus,
.left-panel .form-select:focus {
  border-color: #95b8ff;
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.14);
}

.generation-cloud-title {
  display: inline-flex;
  align-items: center;
  margin: 0 0 10px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #d8e5fb;
  background: linear-gradient(180deg, #f2f7ff 0%, #eaf2ff 100%);
  color: #2f4f88;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 4px 10px rgba(49, 94, 227, 0.08);
}

.generation-cloud-title--left { margin-bottom: 10px; }

.left-headers {
  padding-left: 1px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: #4d5f82;
  text-transform: uppercase;
}

.site-project-select { max-width: 100%; }

.stream-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  border: 1px solid #d7e2f6;
  background: #f7faff;
  color: #3d5478;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.01em;
  text-transform: uppercase;
  padding: 5px 10px;
}

.stream-type-badge__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
  opacity: 0.9;
}

.stream-type-badge--wordpress { background: #f5f9ff; border-color: #cfe1ff; color: #2f5e9e; }
.stream-type-badge--telegram { background: #eef9ff; border-color: #b9e8ff; color: #0d6b94; }
.stream-type-badge--dzen { background: #fff6ef; border-color: #ffd9bc; color: #a55b19; }

/* Tabs */
.gen-tabs {
  border-bottom: 1px solid #dce6f8;
  margin-bottom: 14px;
  gap: 4px;
}

.gen-tabs .nav-link {
  border: 1px solid transparent;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  color: #4f6789;
  font-weight: 700;
  font-size: 13px;
  padding: 8px 14px;
  background: transparent;
}

.gen-tabs .nav-link:hover { border-color: #e3ebf8 #e3ebf8 #dce6f8; color: #2f4f88; }

.gen-tabs .nav-link.active {
  color: #1b4f93;
  background: #ffffff;
  border-color: #dce6f8 #dce6f8 #ffffff;
}

.tab-pane-body { display: flex; flex-direction: column; gap: 14px; }

/* Schedule steps (auto-generation tab) */
.schedule-step {
  border: 1px solid #e3ebf8;
  border-radius: 14px;
  background: #fcfdff;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.step-head { display: flex; align-items: center; gap: 10px; }

.step-num {
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 13px;
  color: #fff;
  background: linear-gradient(135deg, #1b4f93, #1e67b7);
}
.step-num--muted { background: #8aa0c4; }

.step-head__text { min-width: 0; }
.step-title { font-weight: 700; color: #1f2d45; font-size: 14px; }
.step-sub { color: #6c7a92; font-size: 12px; }

.step-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-left: 38px;
}

.schedule-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px 16px;
}

.schedule-field { display: flex; flex-direction: column; gap: 4px; }

.schedule-global {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
  padding-top: 12px;
  border-top: 1px solid #e3ebf8;
}

.schedule-switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  min-height: 24px;
  padding-left: 0;
}

.schedule-switch .form-check-input,
.mode-switch .form-check-input,
.rss-switch .form-check-input {
  flex: 0 0 auto;
  width: 2.35rem;
  height: 1.25rem;
  margin: 0;
  border: 1px solid #b9c8de;
  background-color: #d6deea;
  box-shadow: none;
  cursor: pointer;
}

.schedule-switch .form-check-input:checked,
.mode-switch .form-check-input:checked,
.rss-switch .form-check-input:checked {
  border-color: #27815d;
  background-color: #2e9b70;
}

.schedule-switch .form-check-input:focus,
.mode-switch .form-check-input:focus,
.rss-switch .form-check-input:focus {
  border-color: #5198df;
  box-shadow: 0 0 0 0.2rem rgba(30, 103, 183, 0.14);
}

.schedule-switch .form-check-label,
.rss-switch .form-check-label { cursor: pointer; }

.mode-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  color: #7a879d;
  font-size: 13px;
}

.mode-switch__control { flex: 0 0 auto; min-height: 24px; padding-left: 0; }
.mode-switch__label--active { color: #2f4f88; font-weight: 700; }

.rss-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  padding-left: 0;
}

.rss-category-select { max-width: 280px; }

.rss-list {
  border: 1px solid #e7edf8;
  border-radius: 12px;
  background: #fbfdff;
  padding: 10px 12px;
  max-height: 260px;
  overflow-y: auto;
}

.rss-item + .rss-item { margin-top: 6px; }

.cmaker-groups { display: flex; flex-direction: column; gap: 14px; }
.cmaker-group { display: flex; flex-direction: column; gap: 8px; }
.cmaker-group--divided { border-top: 1px solid #e3ebf8; padding-top: 14px; }
.cmaker-group__head { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.cmaker-group__title { font-weight: 700; color: #2f4f88; font-size: 13px; }
.cmaker-group__sub { color: #6c7a92; font-size: 12px; }

.page-footer {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border-top: 1px solid #e7edf8;
  background: #ffffff;
}

.page-footer .btn {
  min-width: 90px;
  padding: 0.3rem 0.65rem;
  font-size: 0.84rem;
}

@media (max-width: 1180px) {
  .left-panel { flex-basis: 42%; }
}

@media (max-width: 920px) {
  .split-body { flex-direction: column; }
  .left-panel { flex: 0 0 auto; max-height: 46%; }
  .right-panel {
    flex: 1 1 auto;
    border-left: 0;
    border-top: 1px solid #dce6f8;
    box-shadow: inset 0 10px 14px -14px rgba(31, 61, 138, 0.45);
  }
  .right-panel::before { display: none; }
  .schedule-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .stream-form-page { padding: 6px; }
  .page-hero { padding: 10px 12px; border-radius: 12px; }
  .form-card { border-radius: 12px; }
  .left-panel, .right-panel { padding: 12px; }
  .page-footer { padding: 10px 12px; }
  .step-body { padding-left: 0; }
}
</style>
